package com.devi3east.bashbuddy.auth.data.api

import com.devi3east.bashbuddy.auth.requests.SignInRequest
import com.devi3east.bashbuddy.auth.requests.SignUpRequest
import com.devi3east.bashbuddy.auth.responses.AuthResponse
import com.devi3east.bashbuddy.auth.responses.AuthResult
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.account.organization.requests.UpdateOrganizationRequest
import com.devi3east.bashbuddy.account.organization.responses.UpdateOrganizationResponse
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.account.profile.requests.UpdateProfileRequest
import com.devi3east.bashbuddy.account.profile.responses.UpdateProfileResponse
import com.devi3east.bashbuddy.leaderboard.domain.models.OrganizationLeaderboardItem
import com.devi3east.bashbuddy.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.json.Json

class AuthApi(
    private val client: HttpClient,
    private val baseUrl: String
) {
    private val json = Json { ignoreUnknownKeys = true }

    suspend fun signUp(request: SignUpRequest): AuthResult<Profile> {
        if (request.email.isBlank()) {
            return AuthResult.EmailBlank()
        }
        if (request.password.isBlank()) {
            return AuthResult.PasswordBlank()
        }
        return try {
            val response = client.post("$baseUrl/signup") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            when (response.status.value) {
                201 -> {
                    AuthResult.Success()
                }
                409 -> {
                    AuthResult.UserAlreadyExists()
                }
                400 -> {
                    AuthResult.UnknownError()
                }
                else -> {
                    AuthResult.UnknownError()
                }
            }
        } catch (e: Exception) {
            AuthResult.UnknownError()
        }
    }

    suspend fun signIn(request: SignInRequest): AuthResult<Profile> {
        if (request.email.isBlank()) return AuthResult.EmailBlank()
        if (request.password.isBlank()) return AuthResult.PasswordBlank()
        return try {
            val response = client.post("$baseUrl/signin") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            val bodyText = response.bodyAsText()
            when (response.status.value) {
                200 -> {
                    val auth = json.decodeFromString<AuthResponse>(bodyText)
                    AuthResult.Authorized(
                        data = auth.profile,
                        accessToken = auth.accessToken,
                        refreshToken = auth.refreshToken
                    )
                }
                401 -> {
                    AuthResult.InvalidCredentials()
                }
                else -> {
                    AuthResult.UnknownError()
                }
            }
        } catch (e: Exception) {
            AuthResult.UnknownError()
        }
    }

    suspend fun authenticate(token: String): AuthResult<Profile> {
        if (token.isBlank()) {
            return AuthResult.Unauthorized()
        }
        return try {
            val response = client.get("$baseUrl/authenticate") {
                header(HttpHeaders.Authorization, "Bearer $token")
            }
            val bodyText = response.bodyAsText()
            when (response.status.value) {
                200 -> {
                    val auth = json.decodeFromString<AuthResponse>(bodyText)
                    AuthResult.Authorized(
                        data = auth.profile,
                        accessToken = auth.accessToken,
                        refreshToken = auth.refreshToken
                    )
                }
                401 -> {
                    AuthResult.Unauthorized()
                }
                else -> {
                    AuthResult.UnknownError()
                }
            }
        } catch (e: Exception) {
            AuthResult.UnknownError()
        }
    }

    suspend fun signOut() {
        try {
            SupabaseClient.client.auth.signOut()
        } catch (_: Exception) {
            // intentionally ignored
        }
        try {
            client.get("$baseUrl/signout")
        } catch (_: Exception) {
            // intentionally ignored
        }
    }

    suspend fun updateProfile(token: String, request: UpdateProfileRequest): UpdateProfileResponse {
        val response = client.put("$baseUrl/profile/update") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        val rawText = response.bodyAsText()
        return if (response.status.isSuccess()) {
            json.decodeFromString(UpdateProfileResponse.serializer(), rawText)
        } else {
            val error = try {
                json.decodeFromString<Map<String, String>>(rawText)["error"] ?: "Unknown error"
            } catch (_: Exception) {
                "Unknown error"
            }
            throw Exception("UpdateProfile failed: $error")
        }
    }

    suspend fun updateOrganization(token: String, request: UpdateOrganizationRequest): UpdateOrganizationResponse {
        val response = client.put("$baseUrl/organization/update") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        val rawText = response.bodyAsText()
        return if (response.status.isSuccess()) {
            json.decodeFromString(UpdateOrganizationResponse.serializer(), rawText)
        } else {
            val error = try {
                json.decodeFromString<Map<String, String>>(rawText)["error"] ?: "Unknown error"
            } catch (_: Exception) {
                "Unknown error"
            }
            throw Exception("UpdateOrganization failed: $error")
        }
    }

    suspend fun getOrganizationById(userId: String): Organization {
        val response = client.get("$baseUrl/organization/detail/$userId")
        return response.body()
    }

    suspend fun getOrganizations(): List<Organization> {
        val response = client.get("$baseUrl/organizations")
        return response.body()
    }

    suspend fun getLeaderboard(): List<OrganizationLeaderboardItem> {
        val response = client.get("$baseUrl/api/leaderboard")
        return response.body()
    }

    suspend fun deactivateAccount(token: String): Boolean {
        return try {
            val response = client.put("$baseUrl/account/deactivate") {
                headers { append(HttpHeaders.Authorization, "Bearer $token") }
                contentType(ContentType.Application.Json)
            }
            response.status.isSuccess()
        } catch (e: Exception) {
            false
        }
    }

    suspend fun reactivateAccount(token: String): Boolean {
        return try {
            val response = client.put("$baseUrl/account/reactivate") {
                headers { append(HttpHeaders.Authorization, "Bearer $token") }
                contentType(ContentType.Application.Json)
            }
            response.status.isSuccess()
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteAccount(token: String): Boolean {
        return try {
            val response = client.delete("$baseUrl/account/delete") {
                headers { append(HttpHeaders.Authorization, "Bearer $token") }
                contentType(ContentType.Application.Json)
            }
            response.status.isSuccess()
        } catch (e: Exception) {
            false
        }
    }

}